import { aiCostShare, formatMoney, subscriptionMonthNet, todayIso, type Pricing } from "@szerviz/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { useApp } from "../context";
import type { OpAiToday, OpAiUsage, OpSubscription, OpWorkshop } from "../data";
import { opSubscriptionState } from "../data/subscription";

/** One row of the monthly AI report: the real cost against the month's subscription fee. */
export interface AiReportRow {
  w: OpWorkshop;
  costHuf: number;
  sharedCostHuf: number;
  chargedHuf: number;
  boughtHuf: number;
  feeNet: number;
  share: number | null;
  flagged: boolean;
}

/** The rows of a month (cost share over alertShare, or cost without a fee, is flagged), dearest first. */
export function aiReportRows(rows: OpWorkshop[], usage: OpAiUsage[], subs: Map<string, OpSubscription>, pricing: Pricing, month: string, alertShare: number): AiReportRow[] {
  const by = new Map(usage.map((u) => [u.workshopId, u]));
  return rows
    .map((w) => {
      const u = by.get(w.id);
      const feeNet = subscriptionMonthNet(opSubscriptionState(w, subs.get(w.id)), pricing, `${month}-01`);
      const costHuf = u?.costHuf ?? 0;
      const { share, flagged } = aiCostShare(costHuf, feeNet, alertShare);
      return { w, costHuf, sharedCostHuf: u?.sharedCostHuf ?? 0, chargedHuf: u?.chargedHuf ?? 0, boughtHuf: u?.boughtHuf ?? 0, feeNet, share, flagged };
    })
    .sort((a, b) => b.costHuf + b.sharedCostHuf - (a.costHuf + a.sharedCostHuf) || a.w.name.localeCompare(b.w.name));
}

/**
 * Operator: today's platform AI cost against the daily cap, and the monthly report per workshop (real AI cost,
 * charged credit, subscription fee, share; orange above the alert share).
 */
export function AiReport({ rows, subs, pricing, alertShare, onOpen }: {
  rows: OpWorkshop[]; subs: Map<string, OpSubscription>; pricing: Pricing; alertShare: number; onOpen: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const [usage, setUsage] = useState<OpAiUsage[] | null>(null);
  const [today, setToday] = useState<OpAiToday | null>(null);
  const [onlyUsed, setOnlyUsed] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const money = (n: number) => formatMoney(n, "HUF", i18n.language);
  const pct = (x: number) => `${Math.round(x * 1000) / 10}%`;

  useEffect(() => {
    let live = true;
    setError(null);
    Promise.all([store.opAiUsage(`${month}-01`), store.opAiToday()])
      .then(([u, d]) => {
        if (!live) return;
        setUsage(u);
        setToday(d);
      })
      .catch((e) => live && setError(e));
    return () => {
      live = false;
    };
  }, [store, month]);

  const all = useMemo(() => (usage ? aiReportRows(rows, usage, subs, pricing, month, alertShare) : []), [rows, usage, subs, pricing, month, alertShare]);
  const shown = onlyUsed ? all.filter((r) => r.costHuf + r.sharedCostHuf + r.chargedHuf > 0) : all;
  const sum = (k: "costHuf" | "sharedCostHuf" | "chargedHuf" | "feeNet") => all.reduce((a, r) => a + r[k], 0);
  const todayShare = today && today.capHuf > 0 ? today.costHuf / today.capHuf : 0;

  return (
    <section className="operator-ai" id="op-ai-report" aria-label={t("operator.aiReport.title")}>
      <h2>{t("operator.aiReport.title")}</h2>
      <ErrorText error={error} />
      {today && (
        <div className="opw-strip" role="group" aria-label={t("operator.aiReport.today")}>
          <div className="opw-fact">
            <span className="opw-fact-label">{t("operator.aiReport.today")}</span>
            <span className={`opw-fact-value${todayShare >= 1 ? " error" : todayShare >= 0.8 ? " warn-text" : ""}`}>
              {t("operator.aiReport.todayValue", { cost: money(today.costHuf), cap: money(today.capHuf), calls: today.calls })}
            </span>
          </div>
          {todayShare >= 1 && <div className="opw-fact"><span className="warn-text">{t("operator.aiReport.paused")}</span></div>}
          {today.alertAt && <div className="opw-fact"><span className="muted">{t("operator.aiReport.alertSent", { time: new Date(today.alertAt).toLocaleTimeString(i18n.language) })}</span></div>}
        </div>
      )}
      <div className="ops-filters">
        <label className="field">
          <span>{t("operator.aiReport.month")}</span>
          <input type="month" value={month} max={todayIso().slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t("operator.aiReport.month")} />
        </label>
        <label className="ops-chip">
          <input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />
          {t("operator.aiReport.onlyUsed")}
        </label>
      </div>
      <p className="muted small">{t("operator.aiReport.intro", { share: pct(alertShare) })}</p>
      {usage && shown.length === 0 && <p className="muted">{t("operator.aiReport.none")}</p>}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="list">
            <thead>
              <tr>
                <th>{t("operator.col.workshop")}</th>
                <th className="num">{t("operator.aiReport.cost")}</th>
                <th className="num">{t("operator.aiReport.shared")}</th>
                <th className="num">{t("operator.aiReport.charged")}</th>
                <th className="num">{t("operator.aiReport.fee")}</th>
                <th className="num">{t("operator.aiReport.share")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.w.id} className={r.flagged ? "warn-row" : undefined}>
                  <td><a href={`#operator/${r.w.id}`} onClick={(e) => { e.preventDefault(); onOpen(r.w.id); }}>{r.w.name}</a></td>
                  <td className="num">{money(r.costHuf)}</td>
                  <td className="num">{money(r.sharedCostHuf)}</td>
                  <td className="num">{money(r.chargedHuf)}</td>
                  <td className="num">{r.feeNet > 0 ? money(r.feeNet) : "–"}</td>
                  <td className={`num${r.flagged ? " warn-text" : ""}`} data-flagged={r.flagged || undefined}>
                    {r.share === null ? (r.flagged ? t("operator.aiReport.noFee") : "–") : pct(r.share)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{t("operator.aiReport.total")}</th>
                <th className="num">{money(sum("costHuf"))}</th>
                <th className="num">{money(sum("sharedCostHuf"))}</th>
                <th className="num">{money(sum("chargedHuf"))}</th>
                <th className="num">{money(sum("feeNet"))}</th>
                <th className="num">{sum("feeNet") > 0 ? pct(sum("costHuf") / sum("feeNet")) : "–"}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}
